import React, { useEffect, useMemo, useState } from 'react';
import { Menu, Badge, Dropdown, List, Avatar, Button, message } from 'antd';
import {
    BellOutlined,
    MailOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    DollarOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import Flex from 'components/shared-components/Flex';
import moment from 'moment';
import NotificationService from 'services/NotificationService';
import { useSelector } from 'react-redux';
import { env } from 'configs/EnvironmentConfig';

const getIcon = icon => {
    switch (icon) {
        case "topup_demand":
            return <DollarOutlined />;
        case 'alert':
            return <WarningOutlined />;
        case 'check':
            return <CheckCircleOutlined />
        default:
            return <MailOutlined />;
    }
}

const getColor = (type) => {
    //available colors: blue, purple, cyan, green, magenta, pink, red, orange , volcano , yellow , geekblue , lime , gold , primary , secondary , success , info , warning , danger , gray , gray-light , gray-lighter , gray-lightest ,gray-dark
    switch (type) {
        case 'topup_demand':
            return 'gold';
        case 'topup_demand_test':
            return 'lime';
        case 'alert':
            return 'red';
        case 'check':
            return 'green';
        case 'info':
            return 'blue';
        case 'warning':
            return 'yellow';
        case 'danger':
            return 'red';
        case 'gray':
            return 'gray';
        default:
            return 'gray';
    }
}

const normalizeResponseData = (payload) => payload?.data || payload || {};

const getNotificationBody = (list, onClickNotification) => {
    return list.length > 0 ?
        <List
            size="small"
            itemLayout="horizontal"
            dataSource={list}
            renderItem={item => (
                <List.Item className="list-clickable" onClick={() => onClickNotification(item)}>
                    <Flex alignItems="center">
                        <div className="pr-3">
                            <Avatar className={`ant-avatar-${getColor(item.notification?.type)}`} icon={getIcon(item.notification?.type)} />
                        </div>
                        <div className="mr-3">
                            <span className={`font-weight-bold ${item.status === 'unread' ? 'text-dark' : 'text-gray-light'}`}>
                                {item.notification?.title || 'Notification'}{' '}
                            </span>
                            <span className="text-gray-light">{item.notification?.message || ''}</span>
                        </div>
                        <small className="ml-auto">{moment(item.createdAt).format('HH:mm')}</small>
                    </Flex>
                </List.Item>
            )}
        />
        :
        <div className="empty-notification">
            <img src="https://gw.alipayobjects.com/zos/rmsportal/sAuJeJzSKbUmHfBQRzmZ.svg" alt="empty" />
            <p className="mt-3">You have viewed all notifications</p>
        </div>;
}

export const NavNotification = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const [visible, setVisible] = useState(false);

    const listRequest = useMemo(() => ({
        pagination: {
            current: 1,
            pageSize: 10,
        },
    }), []);

    const { data: notificationsResponse, isLoading: isLoadingNotifications } = useQuery(
        NotificationService.list(listRequest)
    );
    const { data: unreadResponse, isLoading: isLoadingUnreadCount } = useQuery(
        NotificationService.getUnreadCount()
    );

    const userCode = useSelector(state => state.auth.code);

    useEffect(() => {
        const socket = new WebSocket(env.WEB_SOCKET_URL);  // Change to your WebSocket server URL

        socket.onopen = () => {
            console.log('Connected to WebSocket server');
            socket.send(JSON.stringify({
                action: 'subscribe',
                user_code: userCode
            }));
        };

        socket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            console.log(message?.data?.type);
            if (message?.data?.type === 'topup_demand' || message?.data?.type === 'topup_demand_test') {
                const audio = new Audio('/audio/notification_1.mp3'); // Adjust path if needed
                audio.play().catch((err) => console.error("Audio play failed:", err));
            } else if (message?.type !== "subscribed") {
                const audio = new Audio('/audio/notification.mp3'); // Adjust path if needed
                audio.play().catch((err) => console.error("Audio play failed:", err));
            }

            queryClient.invalidateQueries({ queryKey: ['get_notification_unread_count'] });
            queryClient.invalidateQueries({ queryKey: ['get_all_notifications'] });
        };

        socket.onclose = () => {
            console.log('Disconnected from WebSocket server');
        };

        // Clean up when the component is unmounted
        return () => {
            socket.close();
        };
    }, [userCode]);

    const notificationsData = normalizeResponseData(notificationsResponse);
    const unreadData = normalizeResponseData(unreadResponse);

    const notifications = notificationsData?.results || [];
    const unreadCountFromList = notifications.filter(item => item.status === 'unread').length;
    const unreadCount = Number.isFinite(unreadData?.unreadCount) ? unreadData.unreadCount : unreadCountFromList;

    const refreshNotifications = () => {
        queryClient.invalidateQueries(['get_all_notifications']);
        queryClient.invalidateQueries(['get_notification_unread_count']);
    };

    const markReadMutation = useMutation({
        mutationFn: NotificationService.markRead,
        onSuccess: refreshNotifications,
        onError: (error) => {
            message.error(error.message || 'Failed to mark notification as read');
        }
    });

    const markAllReadMutation = useMutation({
        mutationFn: NotificationService.markAllRead,
        onSuccess: () => {
            refreshNotifications();
            message.success('All notifications marked as read');
        },
        onError: (error) => {
            message.error(error.message || 'Failed to mark notifications as read');
        }
    });

    const handleVisibleChange = (flag) => {
        setVisible(flag);
    }

    const handleNotificationClick = (item) => {
        if (item.status === 'unread' && item.id) {
            markReadMutation.mutate(item.id);
        }
        console.log(item?.notification?.type);
        if (item?.notification?.type === 'topup_demand' || item?.notification?.type === 'topup_demand_test') {
            history.push('/app/credit-request/list');
        } else {
            history.push('/app/profile/notification');
        }

    }

    const isReadAllLoading = markAllReadMutation.isLoading;
    const isDropdownLoading = isLoadingNotifications || isLoadingUnreadCount;

    const notificationList = (
        <div className="nav-dropdown nav-notification">
            <div className="nav-notification-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Notification</h4>
                <div>
                    <Button className="text-primary" type="text" onClick={() => markAllReadMutation.mutate()} size="small" loading={isReadAllLoading}>
                        Read all
                    </Button>
                </div>
            </div>
            <div className="nav-notification-body">
                {getNotificationBody(notifications, handleNotificationClick)}
            </div>
            {
                notifications.length > 0 ?
                    <div className="nav-notification-footer">
                        <Button className="d-block w-100" type="link" onClick={() => {
                            setVisible(false);
                            history.push('/app/profile/notification');
                        }}>
                            View all
                        </Button>
                    </div>
                    :
                    null
            }
        </div>
    );

    return (
        <Dropdown
            placement="bottomRight"
            overlay={notificationList}
            onVisibleChange={handleVisibleChange}
            visible={visible}
            trigger={['click']}
        >
            <Menu mode="horizontal">
                <Menu.Item key="notification">
                    <Badge count={isDropdownLoading ? 0 : unreadCount}>
                        <BellOutlined className="nav-icon mx-auto" type="bell" />
                    </Badge>
                </Menu.Item>
            </Menu>
        </Dropdown>
    )
}


export default NavNotification;
